import { useEffect, useMemo, useState } from 'react';
import {
  WHO_LENGTH_CM,
  WHO_WEIGHT_KG,
  addDays,
  can,
  describeEvent,
  formatClock,
  formatDuration,
  localDate,
  whoAt,
  type DayStat,
} from '@bb/shared/core';
import { api, type StatsDto } from '../api';
import { BarChart, Breakdown, GrowthChart, HeatLegend, Legend, MonthCalendar, RhythmMap, YearCalendar, type Bar, type GrowthPoint } from '../charts';
import { Icon, Seg } from '../components';
import { categoryOf, plural } from '../lib';
import { METRICS, avgOf, sumOf, type MetricKey } from '../metrics';
import { MONTHS_TINY, PREV_LABEL, SAME_LABEL, WEEKDAYS_SHORT, daysBetween, longDate, monthsOf, periodLabel, periodOf, shiftPeriod, shortDate, weekday, type PeriodKind } from '../period';
import { useApp } from '../state';
import { ChildHeader } from './ChildHeader';

const KINDS: { value: PeriodKind; label: string }[] = [
  { value: 'day', label: 'День' },
  { value: 'week', label: 'Неделя' },
  { value: 'month', label: 'Месяц' },
  { value: 'year', label: 'Год' },
];

const LEGEND = [
  { cat: 'sleep' as const, label: 'сон' },
  { cat: 'feed' as const, label: 'кормление' },
  { cat: 'diaper' as const, label: 'подгузник' },
  { cat: 'walk' as const, label: 'прогулка' },
];

const fmt1 = (v: number) => (Math.round(v * 10) / 10).toLocaleString('ru-RU');

/** Загрузка статистики; перезагрузка после любых изменений записей. */
function useStats(childId: string, from: string, to: string) {
  const { version, fail } = useApp();
  const [data, setData] = useState<StatsDto | null>(null);
  useEffect(() => {
    let alive = true;
    setData((d) => (d && d.from === from ? d : null));
    api.stats(childId, from, to).then(
      (d) => alive && setData(d),
      (e) => alive && fail(e),
    );
    return () => {
      alive = false;
    };
  }, [childId, from, to, version, fail]);
  return data && data.from === from ? data : null;
}

export function Progress() {
  const ctx = useApp();
  const { child, family } = ctx;
  const today = localDate(Date.now(), family.tz);
  const [kind, setKind] = useState<PeriodKind>('week');
  const [anchor, setAnchor] = useState(today);
  const [metric, setMetric] = useState<MetricKey>('sleep');
  const [sel, setSel] = useState<string | null>(null);

  const period = periodOf(kind, anchor);
  const prev = kind === 'day' ? { from: addDays(period.from, -7), to: addDays(period.from, -1) } : kind === 'year' ? null : shiftPeriod(period, -1);
  const birth = child.birthDate;
  const fetchFrom = [prev?.from ?? period.from, birth].sort().at(-1)!;
  const fetchTo = period.to;
  const data = useStats(child.id, fetchFrom > fetchTo ? period.from : fetchFrom, fetchTo);

  useEffect(() => setSel(null), [kind, anchor, metric]);

  const inRange = (d: DayStat, f: string, t: string) => d.date >= f && d.date <= t && d.date >= birth;
  const days = useMemo(() => (data ? data.days.filter((d) => inRange(d, period.from, period.to)) : []), [data, period.from, period.to]);
  const prevDays = useMemo(() => (data && prev ? data.days.filter((d) => inRange(d, prev.from, prev.to)) : []), [data, prev?.from, prev?.to]);
  // Сегодняшний день ещё не закончился — в средних и сравнениях его не учитываем (если есть другие дни).
  const full = (ds: DayStat[]) => (ds.some((d) => d.hasData && d.date < today) ? ds.filter((d) => d.date < today) : ds);
  const avgDays = useMemo(() => full(days), [days, today]);
  const hasBottle = days.some((d) => d.bottleMl > 0);
  const metricKey: MetricKey = metric === 'bottle' && !hasBottle ? 'feed' : metric;
  const M = METRICS[metricKey];
  const withData = days.filter((d) => d.hasData);
  const canPrev = period.from > birth;
  const canNext = period.to < today;

  const goDay = (date: string) => {
    setKind('day');
    setAnchor(date);
  };
  const go = (dir: -1 | 1) => setAnchor(shiftPeriod(period, dir).from);

  /* ---------- Плитки показателей ---------- */
  const tiles: { key: MetricKey; sub: string }[] = [
    { key: 'sleep', sub: (() => { const n = avgOf(avgDays, (d) => d.nightSleepMin); return n != null ? `ночью ${formatDuration(n)}` : ''; })() },
    { key: 'feed', sub: (() => { const b = avgOf(avgDays, (d) => d.breastCount); const bo = avgOf(avgDays, (d) => d.bottleCount); return b != null ? `грудь ${fmt1(b)} · бут. ${fmt1(bo ?? 0)}` : ''; })() },
    ...(hasBottle ? [{ key: 'bottle' as MetricKey, sub: (() => { const c = avgOf(avgDays, (d) => d.bottleCount); return c != null ? `${fmt1(c)} в день · по ${Math.round(sumOf(avgDays, (d) => d.bottleMl) / Math.max(1, sumOf(avgDays, (d) => d.bottleCount)))} мл за раз` : ''; })() }] : []),
    { key: 'diaper', sub: (() => { const w = avgOf(avgDays, (d) => d.wetCount); const dr = avgOf(avgDays, (d) => d.dirtyCount); return w != null ? `мокр. ${fmt1(w)} · гряз. ${fmt1(dr ?? 0)}` : ''; })() },
    { key: 'walk', sub: (() => { const c = avgOf(avgDays, (d) => d.walkCount); return c != null ? `${fmt1(c)} в день · ${formatDuration(sumOf(avgDays, (d) => d.walkMin) / Math.max(1, sumOf(avgDays, (d) => d.walkCount)))} каждая` : ''; })() },
  ];

  /* ---------- Столбцы ---------- */
  const bars: Bar[] = useMemo(() => {
    if (kind === 'year') {
      return monthsOf(period).map((ym, i) => {
        const ds = days.filter((d) => d.date.startsWith(ym));
        const v = avgOf(full(ds), M.value);
        return { key: ym, label: MONTHS_TINY[i]!, value: v, strong: today.startsWith(ym) };
      });
    }
    const out: Bar[] = [];
    for (let d = period.from; d <= period.to; d = addDays(d, 1)) {
      const st = days.find((x) => x.date === d);
      const label = kind === 'week' ? WEEKDAYS_SHORT[weekday(d)]! : String(Number(d.slice(8)));
      out.push({ key: d, label, value: st?.hasData ? M.value(st) : null, strong: d === today });
    }
    return out;
  }, [kind, days, period.from, period.to, M, today]);

  const avg = avgOf(avgDays, M.value);
  const prevAvg = prev ? avgOf(full(prevDays), M.value) : null;
  const selDay = sel && sel.length === 10 ? days.find((d) => d.date === sel) ?? null : null;

  /* ---------- Рост ---------- */
  const [growthKind, setGrowthKind] = useState<'weight' | 'height' | 'head'>('weight');
  const growth = useMemo(() => {
    if (!data) return null;
    const ageM = (date: string) => Math.max(0, (Date.parse(date) - Date.parse(birth)) / 86400000 / 30.4375);
    const pts: GrowthPoint[] = [];
    for (const m of data.measures) {
      const v = growthKind === 'weight' ? (m.weightG != null ? m.weightG / 1000 : null) : growthKind === 'height' ? m.heightCm ?? null : m.headCm ?? null;
      if (v != null) pts.push({ months: ageM(m.date), value: v, date: m.date });
    }
    if (growthKind === 'weight' && child.birthWeightG && !pts.some((p) => p.months < 0.2)) pts.unshift({ months: 0, value: child.birthWeightG / 1000, date: birth });
    const nowM = ageM(today);
    const maxMonths = Math.min(24, Math.max(6, Math.ceil(nowM + 0.5)));
    const sex = child.sex;
    const table = growthKind === 'weight' ? WHO_WEIGHT_KG : growthKind === 'height' ? WHO_LENGTH_CM : null;
    const band = sex && table ? (m: number) => whoAt(table[sex], m) : null;
    return { pts, maxMonths, band };
  }, [data, growthKind, birth, today, child.birthWeightG, child.sex]);
  const growthFmt = (v: number) => (growthKind === 'weight' ? `${v.toFixed(2).replace('.', ',')} кг` : `${String(Math.round(v * 10) / 10).replace('.', ',')} см`);

  const [healthAll, setHealthAll] = useState(false);
  const canWrite = can(family.role, 'events.write');

  return (
    <div className="stack">
      <ChildHeader />

      <Seg<PeriodKind> label="Период" value={kind} options={KINDS} onChange={(k) => setKind(k)} />

      <div className="row period-nav">
        <button type="button" className="icon-btn" aria-label="Предыдущий период" disabled={!canPrev} onClick={() => go(-1)}>
          <Icon name="left" size={18} />
        </button>
        <div className="grow" style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 700 }} data-testid="period-label">
            {periodLabel(period, today)}
          </div>
          {kind !== 'day' && data && <div className="tiny muted">{withData.length ? `${withData.length} ${plural(withData.length, 'день', 'дня', 'дней')} с записями` : 'нет записей'}</div>}
        </div>
        <button type="button" className="icon-btn" aria-label="Следующий период" disabled={!canNext} onClick={() => go(1)}>
          <Icon name="right" size={18} />
        </button>
      </div>
      {(anchor !== today || kind === 'day') && period.to < today && (
        <button type="button" className="link-btn" onClick={() => setAnchor(today)}>
          Вернуться к текущему периоду
        </button>
      )}

      {period.to < birth ? (
        <div className="card center" style={{ minHeight: 160 }}>
          <p className="muted">{child.name} ещё не родился в этот период</p>
        </div>
      ) : !data ? (
        <>
          <div className="tiles2">
            <div className="skeleton" />
            <div className="skeleton" />
            <div className="skeleton" />
            <div className="skeleton" />
          </div>
          <div className="skeleton" style={{ minHeight: 220 }} />
        </>
      ) : (
        <>
          {/* Плитки — они же переключатель показателя для графиков */}
          <div className="tiles2" role="group" aria-label="Показатель">
            {tiles.map(({ key, sub }) => {
              const m = METRICS[key];
              const a = kind === 'day' ? (days[0]?.hasData ? m.value(days[0]) : null) : avgOf(avgDays, m.value);
              const p = prev ? avgOf(full(prevDays), m.value) : null;
              const diff = a != null && p != null ? a - p : null;
              const same = diff != null && Math.abs(diff) < Math.max(1, Math.abs(p ?? 0) * 0.03);
              return (
                <button key={key} type="button" className={`kpi cat-${m.cat}`} aria-pressed={metricKey === key} onClick={() => setMetric(key)}>
                  <span className="label">
                    {m.label}
                    {kind !== 'day' ? ' · в день' : ''}
                  </span>
                  <span className="value">{a == null ? '—' : m.format(a)}</span>
                  {sub && kind !== 'day' && <span className="tiny">{sub}</span>}
                  {kind === 'day' && days[0]?.hasData && key === 'sleep' && <span className="tiny">ночью {formatDuration(days[0].nightSleepMin)}</span>}
                  {diff != null && (
                    <span className="tiny delta">
                      {same ? `≈ ${SAME_LABEL[kind]}` : `${m.delta(diff)} ${PREV_LABEL[kind]}`}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {withData.length === 0 && (
            <div className="card center" style={{ minHeight: 120 }}>
              <p className="muted">{kind === 'day' ? 'В этот день записей нет' : 'За этот период записей нет'}</p>
              {kind === 'day' && (
                <button type="button" className="btn btn-soft" onClick={() => ctx.openDiary(period.from)}>
                  Открыть в дневнике
                </button>
              )}
            </div>
          )}

          {/* ---------- День: полоса суток и подробности ---------- */}
          {kind === 'day' && days[0]?.hasData && (
            <section className="card stack" aria-label="Сутки">
              <span className="section-title">Как прошли сутки</span>
              <div className="strip big">
                {(days[0].strip ?? []).map((s, i) => (
                  <span key={i} style={{ left: `${(s.from / 1440) * 100}%`, width: `${Math.max(0.6, ((s.to - s.from) / 1440) * 100)}%`, background: `var(--c-${s.category})` }} />
                ))}
              </div>
              <div className="strip-scale">
                {[0, 3, 6, 9, 12, 15, 18, 21, 24].map((h) => (
                  <span key={h}>{h}</span>
                ))}
              </div>
              <Legend items={LEGEND} />
              <DayFacts d={days[0]} />
              <button type="button" className="btn btn-secondary" onClick={() => ctx.openDiary(period.from)}>
                <Icon name="diary" size={18} /> Все записи дня в дневнике
              </button>
            </section>
          )}

          {/* ---------- Неделя / месяц / год: столбцы ---------- */}
          {kind !== 'day' && withData.length > 0 && (
            <section className="card stack" aria-label={`${M.title} по ${kind === 'year' ? 'месяцам' : 'дням'}`}>
              <div className="row">
                <span className="section-title grow">
                  {M.title} {kind === 'year' ? 'по месяцам (в среднем за день)' : 'по дням'}
                </span>
                {prevAvg != null && avg != null && <span className="tiny muted">раньше {M.format(prevAvg)}</span>}
              </div>
              <BarChart
                bars={bars}
                cat={M.cat}
                avg={avg}
                format={M.format}
                short={M.short}
                selected={sel}
                onSelect={setSel}
                stepMin={metricKey === 'sleep' || metricKey === 'walk' ? 60 : undefined}
              />
              {!sel && <p className="tiny muted">Нажмите на столбец, чтобы увидеть подробности {kind === 'year' ? 'месяца' : 'дня'}.</p>}
              {selDay && (
                <div className="detail stack">
                  <div className="row">
                    <span className="grow" style={{ fontWeight: 700 }}>
                      {WEEKDAYS_SHORT[weekday(selDay.date)]}, {longDate(selDay.date)}
                    </span>
                    <button type="button" className="icon-btn sm" aria-label="Закрыть" onClick={() => setSel(null)}>
                      <Icon name="close" size={14} />
                    </button>
                  </div>
                  <DayFacts d={selDay} />
                  <div className="tiles2">
                    <button type="button" className="btn btn-soft" onClick={() => goDay(selDay.date)}>
                      Подробнее
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={() => ctx.openDiary(selDay.date)}>
                      В дневник
                    </button>
                  </div>
                </div>
              )}
              {sel && sel.length === 7 && (
                <div className="detail stack">
                  <div className="row">
                    <span className="grow" style={{ fontWeight: 700 }}>
                      {periodLabel(periodOf('month', `${sel}-01`), '0000-00-00')}
                    </span>
                    <span className="small">{(() => { const v = bars.find((b) => b.key === sel)?.value; return v == null ? 'нет записей' : `${M.format(v)} в день`; })()}</span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-soft"
                    onClick={() => {
                      setKind('month');
                      setAnchor(`${sel}-01`);
                    }}
                  >
                    Открыть месяц
                  </button>
                </div>
              )}
              <MetricDetails metric={metricKey} days={avgDays} />
            </section>
          )}

          {/* ---------- Ритм суток ---------- */}
          {(kind === 'week' || kind === 'month') && withData.length > 0 && (
            <section className="card stack" aria-label="Ритм суток">
              <span className="section-title">Ритм суток</span>
              <RhythmMap
                rows={days.map((d) => ({
                  date: d.date,
                  label: kind === 'week' ? `${WEEKDAYS_SHORT[weekday(d.date)]} ${Number(d.date.slice(8))}` : String(Number(d.date.slice(8))),
                  strip: d.strip ?? [],
                  empty: !d.hasData,
                }))}
                onSelect={goDay}
              />
              <Legend items={LEGEND} />
              <p className="tiny muted">Каждая строка — одни сутки. Видно, как складывается режим: когда малыш спит, ест и гуляет.</p>
            </section>
          )}

          {/* ---------- Календарь ---------- */}
          {(kind === 'month' || kind === 'year') && withData.length > 0 && (
            <section className="card stack" aria-label="Календарь">
              <div className="row">
                <span className="section-title grow">Календарь · {M.label.toLowerCase()}</span>
                <HeatLegend cat={M.cat} />
              </div>
              {kind === 'month' ? (
                <MonthCalendar
                  ym={period.from.slice(0, 7)}
                  values={new Map(days.filter((d) => d.hasData).map((d) => [d.date, M.value(d)]))}
                  max={Math.max(...avgDays.filter((d) => d.hasData).map(M.value), 1)}
                  min={Math.min(...avgDays.filter((d) => d.hasData).map(M.value))}
                  cat={M.cat}
                  today={today}
                  birthDate={birth}
                  format={M.format}
                  onSelect={goDay}
                />
              ) : (
                <YearCalendar
                  year={Number(period.from.slice(0, 4))}
                  values={new Map(days.filter((d) => d.hasData).map((d) => [d.date, M.value(d)]))}
                  max={Math.max(...avgDays.filter((d) => d.hasData).map(M.value), 1)}
                  min={Math.min(...avgDays.filter((d) => d.hasData).map(M.value))}
                  cat={M.cat}
                  today={today}
                  birthDate={birth}
                  format={M.format}
                  onSelect={goDay}
                  onMonth={(ym) => {
                    setKind('month');
                    setAnchor(`${ym}-01`);
                  }}
                />
              )}
              <p className="tiny muted">Чем насыщеннее цвет, тем больше. Нажмите на день, чтобы открыть его.</p>
            </section>
          )}

          {/* ---------- Рост и вес ---------- */}
          <section className="card stack" aria-label="Рост и вес">
            <span className="section-title">Рост и вес</span>
            <Seg
              label="Замер"
              value={growthKind}
              options={[
                { value: 'weight', label: 'Вес' },
                { value: 'height', label: 'Рост' },
                { value: 'head', label: 'Голова' },
              ]}
              onChange={setGrowthKind}
            />
            {growth && growth.pts.length > 0 ? (
              <>
                <GrowthLatest pts={growth.pts} band={growth.band} fmt={growthFmt} />
                <GrowthChart points={growth.pts} band={growth.band} cat="walk" format={growthFmt} maxMonths={growth.maxMonths} />
                <p className="tiny muted">
                  {growth.band
                    ? 'Закрашенный коридор — нормы ВОЗ для этого возраста (от −2 до +2 стандартных отклонений), пунктир — середина нормы.'
                    : growthKind === 'head'
                      ? 'Точки — ваши замеры окружности головы.'
                      : 'Укажите пол малыша в настройках — появится сравнение с нормами ВОЗ.'}
                </p>
              </>
            ) : (
              <div className="center" style={{ minHeight: 100, gap: 8 }}>
                <p className="muted small">Замеров пока нет. Добавьте вес и рост — появится график развития.</p>
                {canWrite && (
                  <button type="button" className="btn btn-soft" onClick={() => ctx.openEditor({ type: 'measure' })}>
                    Добавить замер
                  </button>
                )}
              </div>
            )}
          </section>

          {/* ---------- Здоровье и заметки ---------- */}
          {data.health.filter((e) => localDate(Date.parse(e.startedAt), family.tz) >= period.from).length > 0 && (
            <section className="card stack" aria-label="Здоровье и заметки">
              <span className="section-title">Здоровье и заметки</span>
              <ul className="plain-list">
                {data.health
                  .filter((e) => localDate(Date.parse(e.startedAt), family.tz) >= period.from)
                  .slice(0, healthAll ? 200 : 6)
                  .map((e) => {
                    const date = localDate(Date.parse(e.startedAt), family.tz);
                    return (
                      <li key={e.id}>
                        <button type="button" className="health-item" onClick={() => ctx.openDiary(date)}>
                          <span className={`bar dot-${categoryOf(e.type)}`} />
                          <span className="grow">
                            <span style={{ display: 'block', fontWeight: 600 }}>{describeEvent(e)}</span>
                            <span className="tiny muted">
                              {shortDate(date)}, {formatClock(Date.parse(e.startedAt), family.tz)}
                              {e.note ? ` · ${e.note}` : ''}
                            </span>
                          </span>
                          <Icon name="right" size={16} />
                        </button>
                      </li>
                    );
                  })}
              </ul>
              {!healthAll && data.health.filter((e) => localDate(Date.parse(e.startedAt), family.tz) >= period.from).length > 6 && (
                <button type="button" className="link-btn" onClick={() => setHealthAll(true)}>
                  Показать все
                </button>
              )}
            </section>
          )}

          {kind !== 'day' && <PeriodTotals days={days} span={daysBetween(period.from, period.to)} />}
        </>
      )}
    </div>
  );
}

/** Все цифры одних суток. */
function DayFacts({ d }: { d: DayStat }) {
  if (!d.hasData) return <p className="small muted">Записей нет</p>;
  const rows: [string, string][] = [
    ['Сон', `${formatDuration(d.sleepMin)} · ночью ${formatDuration(d.nightSleepMin)}, днём ${formatDuration(d.daySleepMin)}`],
    ['Самый длинный сон', formatDuration(d.longestSleepMin)],
    ['Кормления', `${d.feedCount}: грудь ${d.breastCount}${d.breastMin ? ` (${formatDuration(d.breastMin)})` : ''}, бутылочка ${d.bottleCount}${d.bottleMl ? ` (${d.bottleMl} мл)` : ''}${d.solidCount ? `, прикорм ${d.solidCount}` : ''}`],
    ['Подгузники', `${d.diaperCount}: мокрых ${d.wetCount}, грязных ${d.dirtyCount}`],
    ['Прогулки', d.walkCount ? `${d.walkCount} · ${formatDuration(d.walkMin)}` : 'не было'],
  ];
  if (d.maxTemp != null) rows.push(['Температура', `до ${d.maxTemp.toFixed(1).replace('.', ',')}°`]);
  if (d.medicineCount) rows.push(['Лекарства', String(d.medicineCount)]);
  return (
    <dl className="facts stacked">
      {rows.map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function MetricDetails({ metric, days }: { metric: MetricKey; days: DayStat[] }) {
  const a = (f: (d: DayStat) => number) => avgOf(days, f) ?? 0;
  if (metric === 'sleep') {
    const longest = Math.max(...days.map((d) => d.longestSleepMin), 0);
    return (
      <Breakdown
        cat="sleep"
        items={[
          { label: 'Ночной сон', value: a((d) => d.nightSleepMin), text: formatDuration(a((d) => d.nightSleepMin)), sub: 'с 20:00 до 08:00' },
          { label: 'Дневной сон', value: a((d) => d.daySleepMin), text: formatDuration(a((d) => d.daySleepMin)) },
          { label: 'Самый длинный сон за период', value: longest, text: formatDuration(longest) },
          ...(() => {
            const n = sumOf(days, (d) => d.settleCount);
            const m = n ? sumOf(days, (d) => d.settleMin) / n : 0;
            return n ? [{ label: 'Укладывание', value: m, text: `${formatDuration(m)} в среднем`, sub: `${n} ${n === 1 ? 'раз' : 'раз'} засекали «начали укладывать»` }] : [];
          })(),
        ]}
      />
    );
  }
  if (metric === 'feed' || metric === 'bottle') {
    const breast = a((d) => d.breastCount);
    const bottle = a((d) => d.bottleCount);
    const solid = a((d) => d.solidCount);
    const bm = sumOf(days, (d) => d.breastMin) / Math.max(1, sumOf(days, (d) => d.breastCount));
    const ml = sumOf(days, (d) => d.bottleMl) / Math.max(1, sumOf(days, (d) => d.bottleCount));
    return (
      <Breakdown
        cat="feed"
        items={[
          { label: 'Грудь', value: breast, text: `${fmt1(breast)} в день`, sub: bm ? `в среднем ${formatDuration(bm)} за кормление` : undefined },
          { label: 'Бутылочка', value: bottle, text: `${fmt1(bottle)} в день`, sub: ml ? `в среднем ${Math.round(ml)} мл за раз · ${Math.round(a((d) => d.bottleMl))} мл в день` : undefined },
          ...(solid ? [{ label: 'Прикорм', value: solid, text: `${fmt1(solid)} в день` }] : []),
        ]}
      />
    );
  }
  if (metric === 'diaper') {
    return (
      <Breakdown
        cat="diaper"
        items={[
          { label: 'Мокрые', value: a((d) => d.wetCount), text: `${fmt1(a((d) => d.wetCount))} в день` },
          { label: 'Грязные', value: a((d) => d.dirtyCount), text: `${fmt1(a((d) => d.dirtyCount))} в день` },
        ]}
      />
    );
  }
  const n = sumOf(days, (d) => d.walkCount);
  const avgWalk = n ? sumOf(days, (d) => d.walkMin) / n : 0;
  const noWalk = days.filter((d) => d.hasData && d.walkCount === 0).length;
  return (
    <Breakdown
      cat="walk"
      items={[
        { label: 'Прогулок в день', value: a((d) => d.walkCount), text: fmt1(a((d) => d.walkCount)) },
        { label: 'Средняя прогулка', value: avgWalk, text: formatDuration(avgWalk) },
        { label: 'Дней без прогулки', value: noWalk, text: String(noWalk) },
      ]}
    />
  );
}

function GrowthLatest({ pts, band, fmt }: { pts: GrowthPoint[]; band: ((m: number) => [number, number, number] | null) | null; fmt: (v: number) => string }) {
  const last = pts[pts.length - 1]!;
  const b = band?.(last.months) ?? null;
  const where = !b ? null : last.value < b[0] ? 'ниже коридора нормы ВОЗ — стоит обсудить с педиатром' : last.value > b[2] ? 'выше коридора нормы ВОЗ — стоит обсудить с педиатром' : 'в коридоре нормы ВОЗ';
  const first = pts[0]!;
  const gain = pts.length > 1 ? last.value - first.value : null;
  return (
    <div className="row" style={{ alignItems: 'flex-end' }}>
      <div className="grow">
        <div className="value" style={{ fontSize: 22 }}>
          {fmt(last.value)}
        </div>
        <div className="tiny muted">
          {longDate(last.date)}
          {where ? ` · ${where}` : ''}
        </div>
      </div>
      {gain != null && gain > 0 && <div className="tiny muted" style={{ textAlign: 'right' }}>+{fmt(gain)}<br />с {longDate(first.date)}</div>}
    </div>
  );
}

/** «3 дн 4 ч» для длинных сумм. */
function longDuration(min: number): string {
  if (min < 48 * 60) return formatDuration(min);
  const d = Math.floor(min / 1440);
  const h = Math.round((min - d * 1440) / 60);
  return `${d} ${plural(d, 'день', 'дня', 'дней')}${h ? ` ${h} ч` : ''}`;
}

function PeriodTotals({ days, span }: { days: DayStat[]; span: number }) {
  const withData = days.filter((d) => d.hasData);
  if (!withData.length) return null;
  const rows: [string, string][] = [
    ['Сон всего', longDuration(sumOf(days, (d) => d.sleepMin))],
    ['Кормлений', String(sumOf(days, (d) => d.feedCount))],
    ['Бутылочка', `${sumOf(days, (d) => d.bottleMl).toLocaleString('ru-RU')} мл`],
    ['Подгузников', String(sumOf(days, (d) => d.diaperCount))],
    ['На прогулках', longDuration(sumOf(days, (d) => d.walkMin))],
  ];
  return (
    <section className="card stack" aria-label="Итого за период">
      <span className="section-title">
        Итого за период · {withData.length} из {span} {plural(span, 'дня', 'дней', 'дней')}
      </span>
      <dl className="facts">
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
